import { create } from 'zustand';
import printTemplateService from '../services/printTemplateService';
import { useAuthStore } from './authStore';

// Local cache so favorites render instantly on load. The server is the source
// of truth; the cache just avoids a flash of un-hearted cards. Mirrors
// aiTemplateFavoritesStore, with its own cache key so the two never collide.
const CACHE_KEY = 'sohozkaj:printtpl-favs';

function loadCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (raw) return new Set(JSON.parse(raw).map(Number));
    } catch { /* ignore */ }
    return new Set();
}

function saveCache(set) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify([...set])); } catch { /* ignore */ }
}

/**
 * Per-user print-template favorites, backed by /api/print-templates/favorites with a
 * localStorage cache. Toggling is optimistic; a failed server call rolls back.
 */
export const usePrintTemplateFavoritesStore = create((set, get) => ({
    favorites: loadCache(),   // Set<templateId>
    loaded: false,

    isFavorite: (id) => get().favorites.has(Number(id)),

    /** Fetch the authoritative list from the server (once per session). */
    load: async () => {
        if (get().loaded) return;
        if (!useAuthStore.getState().token) { set({ loaded: true }); return; }
        try {
            const res = await printTemplateService.favorites();
            const next = new Set((res?.data || []).map(Number));
            saveCache(next);
            set({ favorites: next, loaded: true });
        } catch {
            // Keep the cached set; mark loaded so we don't spin.
            set({ loaded: true });
        }
    },

    /** Optimistically toggle, then sync with the server (rollback on failure). */
    toggle: async (templateId) => {
        const id = Number(templateId);
        const prev = get().favorites;
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        set({ favorites: next });
        saveCache(next);

        try {
            const res = await printTemplateService.toggleFavorite(id);
            // Reconcile with the server's authoritative state.
            const synced = new Set(get().favorites);
            res?.data?.isFavorite ? synced.add(id) : synced.delete(id);
            saveCache(synced);
            set({ favorites: synced });
        } catch {
            set({ favorites: prev });   // rollback
            saveCache(prev);
        }
    },
}));
